import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Trophy } from "lucide-react";
import AppBackControl from "../components/ui/AppBackControl";
import SpinWheel from "../components/bond-wheel/SpinWheel";
import Countdown from "../components/bond-wheel/Countdown";
import BondWheelDashboard from "../components/bond-wheel/BondWheelDashboard";
import {
  bondWheelLeave,
  fetchBondWheelGame,
  fetchBondWheelGames,
  selectBondWheelActionLoading,
  selectBondWheelConstants,
  selectBondWheelGameById,
  selectBondWheelLastSettledGame,
  selectMyBondWheelGames,
  setBondWheelSelectedGame,
} from "../redux/bondWheelSlice";
import {
  formatCoin,
  getEntryUserId,
  getGameMinimumPlayers,
  getGameNumberOfWinners,
  getGameWheelEntries,
  getGameWinnerEntries,
  getGameWinnerName,
  getGameWinningSliceIndices,
  getSettledWheelRotation,
  getUserWinnerEntry,
} from "../utils/bondWheelHelpers";
import { initBondWheelSocket } from "../redux/bondWheelSocket";
import { store } from "../../redux/store";
import "../bond-wheel.css";

const WHEEL_SIZE = 280;
const LIVE_GAME_STATUSES = ["Active", "Locked"];
const SPIN_RESULT_DELAY_MS = 2400;
const SPIN_POLL_MS = 3000;

const isTerminalGame = (status) => status === "Settled" || status === "Cancelled";

const BondWheelPlayground = () => {
  const { gameId: routeGameId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = useSelector((s) => s.appAuth?.user?._id);

  const [selectedGameId, setSelectedGameId] = useState(routeGameId || null);
  const [leaveConfirmGame, setLeaveConfirmGame] = useState(null);
  const [leavingGameId, setLeavingGameId] = useState(null);
  const [winningSliceIndex, setWinningSliceIndex] = useState(null);
  const [winningSliceIndices, setWinningSliceIndices] = useState([]);
  const [isWheelSpinning, setIsWheelSpinning] = useState(false);
  const [toast, setToast] = useState(null);

  const lastHandledSettleRef = useRef(null);
  const settlePresentationRef = useRef(null);
  const settleDelayRef = useRef(null);

  const myGames = useSelector((state) => selectMyBondWheelGames(state, userId));
  const constants = useSelector(selectBondWheelConstants);
  const lastSettled = useSelector(selectBondWheelLastSettledGame);
  const actionLoading = useSelector(selectBondWheelActionLoading);
  const game = useSelector((state) => selectBondWheelGameById(state, selectedGameId));

  const sortedMyGames = useMemo(() => {
    const list = (myGames || []).slice();
    list.sort((a, b) => {
      const aLive = LIVE_GAME_STATUSES.includes(a.status) ? 0 : 1;
      const bLive = LIVE_GAME_STATUSES.includes(b.status) ? 0 : 1;
      if (aLive !== bLive) return aLive - bLive;
      if (a.status === "Settled" && b.status === "Settled") {
        return new Date(b.settledAt || b.deadline) - new Date(a.settledAt || a.deadline);
      }
      return new Date(a.deadline) - new Date(b.deadline);
    });
    return list;
  }, [myGames]);

  useEffect(() => {
    initBondWheelSocket(dispatch, store.getState);
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchBondWheelGames({ limit: 60 }));
    dispatch(fetchBondWheelGames({ status: "Settled", limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (routeGameId) setSelectedGameId(String(routeGameId));
  }, [routeGameId]);

  useEffect(() => {
    if (selectedGameId) return;
    if (sortedMyGames.length === 0) return;
    setSelectedGameId(String(sortedMyGames[0]._id));
  }, [selectedGameId, sortedMyGames]);

  useEffect(() => {
    if (!selectedGameId) return;
    dispatch(fetchBondWheelGame({ gameId: selectedGameId }));
  }, [dispatch, selectedGameId]);

  useEffect(() => {
    setIsWheelSpinning(false);
    setWinningSliceIndex(null);
    setWinningSliceIndices([]);
    settlePresentationRef.current = null;
  }, [selectedGameId]);

  const presentSettleResult = useCallback((source) => {
    if (!source?._id) return;

    const presentationKey = `${source._id}-${source.status}-${(getGameWinningSliceIndices(source).join(",") || source.winningSliceIndex) ?? "x"}`;
    if (settlePresentationRef.current === presentationKey) return;
    settlePresentationRef.current = presentationKey;

    const sliceIndices = getGameWinningSliceIndices(source);
    if (source.status === "Settled" && sliceIndices.length > 0) {
      setWinningSliceIndices(sliceIndices);
      setWinningSliceIndex(sliceIndices[0]);
    }

    setIsWheelSpinning(true);
    if (settleDelayRef.current) clearTimeout(settleDelayRef.current);

    settleDelayRef.current = setTimeout(() => {
      setIsWheelSpinning(false);
      if (source.status === "Cancelled") {
        setToast("Wheel canceled.");
      }
    }, SPIN_RESULT_DELAY_MS);
  }, []);

  useEffect(() => {
    if (!game) {
      setWinningSliceIndex(null);
      setWinningSliceIndices([]);
      setIsWheelSpinning(false);
      return;
    }

    const sliceIndices = getGameWinningSliceIndices(game);

    if (game.status === "Settled" && sliceIndices.length > 0) {
      setWinningSliceIndices(sliceIndices);
      setWinningSliceIndex(sliceIndices[0]);
      if (isWheelSpinning) presentSettleResult(game);
      return;
    }

    if (game.status === "Cancelled" && isWheelSpinning) {
      presentSettleResult(game);
      return;
    }

    if (game.status !== "Settled" && game.status !== "Cancelled") {
      setWinningSliceIndex(null);
      setWinningSliceIndices([]);
    }
  }, [game?._id, game?.status, game?.winningSliceIndex, game?.winningSliceIndices, isWheelSpinning, presentSettleResult]);

  useEffect(() => {
    if (!game || !selectedGameId || isTerminalGame(game.status)) return undefined;
    if (!LIVE_GAME_STATUSES.includes(game.status)) return undefined;

    const deadlineMs = new Date(game.deadline).getTime();
    if (!Number.isFinite(deadlineMs)) return undefined;

    const msUntilDeadline = deadlineMs - Date.now();

    if (msUntilDeadline <= 0) {
      dispatch(fetchBondWheelGame({ gameId: selectedGameId }));
      const interval = setInterval(() => {
        dispatch(fetchBondWheelGame({ gameId: selectedGameId }));
      }, SPIN_POLL_MS);
      return () => clearInterval(interval);
    }

    const timer = setTimeout(() => {
      setIsWheelSpinning(true);
      dispatch(fetchBondWheelGame({ gameId: selectedGameId }));
    }, msUntilDeadline);

    return () => clearTimeout(timer);
  }, [dispatch, game?._id, game?.deadline, game?.status, selectedGameId]);

  useEffect(() => {
    if (!isWheelSpinning || !selectedGameId) return undefined;
    const interval = setInterval(() => {
      dispatch(fetchBondWheelGame({ gameId: selectedGameId }));
    }, SPIN_POLL_MS);
    return () => clearInterval(interval);
  }, [dispatch, isWheelSpinning, selectedGameId]);

  useEffect(
    () => () => {
      if (settleDelayRef.current) clearTimeout(settleDelayRef.current);
    },
    []
  );

  useEffect(() => {
    if (!lastSettled || !selectedGameId) return;
    if (String(lastSettled._id) !== String(selectedGameId)) return;
    if (!isWheelSpinning) return;
    if (lastHandledSettleRef.current === String(lastSettled._id) + lastSettled.status) return;
    lastHandledSettleRef.current = String(lastSettled._id) + lastSettled.status;

    if (lastSettled.status === "Settled") {
      const indices = getGameWinningSliceIndices(lastSettled);
      if (indices.length > 0) {
        setWinningSliceIndices(indices);
        setWinningSliceIndex(indices[0]);
      }
      presentSettleResult(lastSettled);
    } else if (lastSettled.status === "Cancelled") {
      presentSettleResult(lastSettled);
    }
  }, [lastSettled, selectedGameId, isWheelSpinning, presentSettleResult]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleSelectGame = useCallback(
    (nextGame) => {
      if (!nextGame?._id) return;
      const nextId = String(nextGame._id);
      setSelectedGameId(nextId);
      dispatch(setBondWheelSelectedGame(nextGame));
      dispatch(fetchBondWheelGame({ gameId: nextId }));
      navigate(`/app/bond-play/bond-wheel/${nextId}`, { replace: true });
    },
    [dispatch, navigate]
  );

  const handleLeave = async (targetGame) => {
    if (!targetGame?._id) return;
    const gameIdToLeave = String(targetGame._id);
    setLeavingGameId(gameIdToLeave);
    try {
      const res = await dispatch(bondWheelLeave({ gameId: targetGame._id })).unwrap();
      setLeaveConfirmGame(null);
      setToast(`${formatCoin(res?.refundAmount || targetGame.entryAmount)} COIN refunded.`);
      if (String(selectedGameId) === gameIdToLeave) {
        const remaining = sortedMyGames.filter((g) => String(g._id) !== gameIdToLeave);
        const nextId = remaining[0]?._id ? String(remaining[0]._id) : null;
        setSelectedGameId(nextId);
        if (nextId) navigate(`/app/bond-play/bond-wheel/${nextId}`, { replace: true });
      }
    } catch (e) {
      setToast(e?.message || "Could not leave.");
    } finally {
      setLeavingGameId(null);
    }
  };

  const wheelEntries = useMemo(() => getGameWheelEntries(game), [game]);
  const wheelParticipants = wheelEntries.map((entry) => ({
    id: entry._id,
    name: entry.user?.name || "Player",
    user: entry.user,
  }));

  const isSettledGame = game?.status === "Settled";
  const settledRotationDeg = useMemo(() => {
    if (!isSettledGame || !Number.isFinite(game?.winningSliceIndex)) return null;
    return getSettledWheelRotation(game.winningSliceIndex, wheelParticipants.length);
  }, [game?.winningSliceIndex, isSettledGame, wheelParticipants.length]);

  const renderWheelStatus = () => {
    if (!game) return null;

    if (game.status === "Settled") {
      const winnerEntries = getGameWinnerEntries(game);
      const numberOfWinners = getGameNumberOfWinners(game);

      if (winnerEntries.length > 1) {
        const names = winnerEntries
          .map((entry) => entry.user?.name || "Player")
          .slice(0, 3)
          .join(", ");
        const suffix = winnerEntries.length > 3 ? ` +${winnerEntries.length - 3}` : "";
        return (
          <div className="bw-settled-banner">
            <Trophy size={14} color="#64B5F6" />
            <span>
              <strong>{numberOfWinners} winners</strong> · {names}
              {suffix}
            </span>
          </div>
        );
      }

      const winnerName = getGameWinnerName(game) || "Unknown";
      const amountWon = Number(
        getUserWinnerEntry(game, userId)?.netPayout ?? game?.winnerNetPayout ?? 0
      );
      return (
        <div className="bw-settled-banner">
          <Trophy size={14} color="#64B5F6" />
          <span>
            <strong>{winnerName}</strong> won <em>{formatCoin(amountWon)} COIN</em>
          </span>
        </div>
      );
    }

    if (game.status === "Cancelled") {
      const minPlayers = getGameMinimumPlayers(game);
      const count = game.activeParticipantCount ?? game.participantCount ?? 0;
      return (
        <span className="bw-cancelled-label">
          Cancelled · {count} joined · min {minPlayers}
        </span>
      );
    }

    return (
      <Countdown
        deadline={game.deadline}
        size="hero"
        tone={game.status === "Locked" ? "lock" : "live"}
        showSpinning={isWheelSpinning}
      />
    );
  };

  if (selectedGameId && !game) {
    return (
      <div className="bw-page bw-playground">
        <div className="bw-empty-state">
          <Loader2 size={24} className="bw-spin-icon" />
          <p>Loading wheel…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bw-page bw-playground">
      <div className="bw-stage-top">
        <AppBackControl to="/app/bond-play/bond-wheel" label="Wheels" />
        <h2 className="bw-playground-title">Bond Wheel</h2>
        <span className="bw-playground-spacer" />
      </div>

      <div className="bw-playground-layout">
        <section className="bw-stage">
          <div className="bw-wheel-zone">
            <SpinWheel
              participants={wheelParticipants}
              size={WHEEL_SIZE}
              winningSliceIndex={winningSliceIndex}
              winningSliceIndices={winningSliceIndices}
              highlightUserId={userId}
              spinning={isWheelSpinning}
              settledRotationDeg={settledRotationDeg}
              settleInstant={isSettledGame && !isWheelSpinning}
            />
          </div>
          <div className="bw-status-slot">{renderWheelStatus()}</div>
        </section>

        <BondWheelDashboard
          myGames={sortedMyGames}
          selectedGameId={selectedGameId}
          onSelectGame={handleSelectGame}
          selectedGame={game}
          userId={userId}
          constants={constants}
          onLeave={(target) => setLeaveConfirmGame(target)}
          leavingGameId={leavingGameId}
        />
      </div>

      {leaveConfirmGame ? (
        <div className="bw-modal-backdrop" role="presentation" onClick={() => !actionLoading && setLeaveConfirmGame(null)}>
          <div className="bw-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Leave this wheel?</h3>
            <p className="bw-join-copy">
              You will be refunded {formatCoin(leaveConfirmGame.entryAmount)} COIN if you leave before lock.
            </p>
            <div className="bw-modal-actions">
              <button type="button" className="bw-btn" onClick={() => setLeaveConfirmGame(null)} disabled={actionLoading}>
                Cancel
              </button>
              <button
                type="button"
                className="bw-btn bw-btn-primary"
                disabled={actionLoading}
                onClick={() => handleLeave(leaveConfirmGame)}
              >
                {actionLoading ? "Leaving…" : "Leave wheel"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? <div className="bw-toast">{toast}</div> : null}
    </div>
  );
};

export default BondWheelPlayground;
